/**
 * Gelände und Baukörper in drei Dimensionen.
 *
 * Standardmässig aus: die Landeskarte ist ein flaches, gezeichnetes Bild, und
 * das soll sie bleiben. Beim Kippen - von Hand oder in der Zielführung - fehlt
 * sie aber spürbar, denn eine geneigte Karte ohne Gelände wirkt wie eine
 * umgelegte Tapete.
 *
 * Das Höhenmodell kommt aus den globalen Terrain Tiles. Für die Darstellung
 * genügt das: ein paar Meter Abweichung an einer Gipfelspitze sieht man in
 * der Schrägsicht nicht. Für *Höhenkurven* wäre dasselbe Modell untauglich -
 * dort zeichnet swisstopo, und das bleibt so.
 */
const DEM = 'gelaende';
const BAUTEN = 'building-3d';

/**
 * Oberhalb dieser Stufe kein Gelände mehr. Zwei Gründe, und beide zählen:
 * im Strassenmassstab ist das Relief ohnehin unsichtbar, und die Kamera gerät
 * dort unter die angehobene Geländeoberfläche - dann zeigt die Karte nichts.
 * Ab hier tragen die Baukörper die Tiefe.
 */
const DEM_BIS = 15.5;

export function createRelief3d({ map, onChange }) {
  let on = false;
  let ueberhoehung = 1.25;
  const nodes = {};

  function auftragen() {
    if (!map.getSource(DEM)) return;
    if (on) {
      const nah = map.getZoom() > DEM_BIS;
      map.setTerrain(nah ? null : { source: DEM, exaggeration: ueberhoehung });
      if (map.getLayer(BAUTEN)) map.setLayoutProperty(BAUTEN, 'visibility', 'visible');
      // Flach gekippt sieht man nichts davon - einmalig aufrichten.
      if (map.getPitch() < 20) map.easeTo({ pitch: 55, duration: 700 });
    } else {
      map.setTerrain(null);
      if (map.getLayer(BAUTEN)) map.setLayoutProperty(BAUTEN, 'visibility', 'none');
    }
  }

  /**
   * Drehen und Kippen gibt es nur in 3D. Die flache Karte ist genordet, und ein
   * versehentlicher Zweifingerwisch soll sie nicht schief stehen lassen.
   */
  function gestenSetzen() {
    if (on) {
      map.dragRotate.enable();
      map.touchZoomRotate.enableRotation();
      map.touchPitch.enable();
      map.keyboard.enableRotation();
    } else {
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
      map.touchPitch.disable();
      map.keyboard.disableRotation();
    }
  }
  gestenSetzen();

  function setOn(next, { stillLassen = false } = {}) {
    const war = on;
    on = Boolean(next);
    auftragen();
    gestenSetzen();
    // Beim Ausschalten wieder gerade hinstellen. Blieb die Karte gekippt, kam man
    // ohne Drehgeste nie mehr aus der Schräglage.
    if (war && !on) map.easeTo({ pitch: 0, bearing: 0, duration: 500 });
    if (nodes.toggle) nodes.toggle.checked = on;
    if (nodes.body) nodes.body.hidden = !on;
    if (!stillLassen) onChange?.(on);
  }

  function mount(host) {
    if (!host) return;
    host.textContent = '';
    host.classList.add('relief3d');

    const row = document.createElement('label');
    row.className = 'check';
    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    const text = document.createElement('span');
    text.textContent = 'Gelände & Gebäude in 3D';
    row.append(toggle, text);

    const body = document.createElement('div');
    body.className = 'relief3d__body';
    body.hidden = true;

    const label = document.createElement('span');
    label.className = 'relief3d__label';
    label.textContent = 'Überhöhung';
    const regler = document.createElement('input');
    regler.type = 'range';
    regler.min = '1';
    regler.max = '2.5';
    regler.step = '0.05';
    regler.value = String(ueberhoehung);
    regler.setAttribute('aria-label', 'Überhöhung des Geländes');
    const wert = document.createElement('em');
    wert.className = 'relief3d__value';
    wert.textContent = `${ueberhoehung.toFixed(2)}×`;

    regler.addEventListener('input', () => {
      ueberhoehung = Number(regler.value);
      wert.textContent = `${ueberhoehung.toFixed(2)}×`;
      if (on && map.getZoom() <= DEM_BIS) map.setTerrain({ source: DEM, exaggeration: ueberhoehung });
    });

    body.append(label, regler, wert);
    host.append(row, body);
    Object.assign(nodes, { toggle, body });

    toggle.addEventListener('change', () => setOn(toggle.checked));
  }

  // Die Grenze liegt mitten im üblichen Zoombereich - beim Überfahren neu setzen.
  let warNah = null;
  map.on('zoom', () => {
    if (!on) return;
    const nah = map.getZoom() > DEM_BIS;
    if (nah !== warNah) { warNah = nah; auftragen(); }
  });

  return {
    mount,
    setOn,
    istAn: () => on,
    /** Nach einem Stilwechsel sind Gelände und Sichtbarkeit weg. */
    reapply: auftragen
  };
}
